import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogBody, Text } from '@/ui';
import { Link } from '@/ui/primitives';
import type { ReactElement } from 'react';

type AppAnnouncementModalProps = {
  announcementKey: string;
  open: boolean;
  onClose: () => void;
};

export const welcomePostOnboardingAnnouncementKey = 'welcome-post-onboarding';

type RenderedAppAnnouncement = {
  title: string;
  content: ReactElement;
};

const renderAnnouncement = (announcementKey: string): RenderedAppAnnouncement | null => {
  switch (announcementKey) {
    case welcomePostOnboardingAnnouncementKey:
      return {
        title: 'Welcome to FlowQA!',
        content: (
          <DialogBody>
            <Text role="body">
              FlowQA is currently in{' '}
              <Text role="bodyStrong" component="span">
                BETA
              </Text>
              . You may see rough edges while we continue to refine the product.
            </Text>
            <Text role="body" color="textSecondary">
              You're currently using the Free plan. Free plans are restricted by uploads, retention
              period, projects and users. Some features are also restricted.
            </Text>
            <Text role="body" color="textSecondary">
              If you're enjoying the app and need more, you can upgrade your plan at any time from
              your Workspace Settings.
            </Text>
            <Text role="body">
              To get started, upload a results file from your test run. FlowQA will turn this into
              meaningful execution runs, suites, tests, and quality trends you can track over time -
              getting more from your results.
            </Text>
            <Text role="body" color="textSecondary">
              Questions, feedback or feature requests? Use the Feedback link in the sidebar or email{' '}
              <Link href="mailto:support@flowqa.com">support@flowqa.com</Link> - we'd love to know
              what you think!
            </Text>
          </DialogBody>
        ),
      };
  }
  return null;
};

export const AppAnnouncementModal = ({
  announcementKey,
  open,
  onClose,
}: AppAnnouncementModalProps): ReactElement | null => {
  const announcement = renderAnnouncement(announcementKey);
  if (!announcement) return null;

  return (
    <ConfirmDialog
      open={open}
      title={announcement.title}
      confirmLabel="Got It"
      confirmColor="primary"
      confirmVariant="contained"
      hideCancel
      disableClose
      onClose={onClose}
      onConfirm={onClose}
    >
      {announcement.content}
    </ConfirmDialog>
  );
};
